import { useEffect, useState, useMemo } from "react";
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
    Alert,
    TableContainer,
} from "@mui/material";

import ChronoSection from "../components/ChronoSection";
import ChronoTitle from "../components/ChronoTitle";

function isToday(isoString) {
    if (!isoString) return true;
    const d = new Date(isoString);
    const now = new Date();
    return (
        d.getUTCFullYear() === now.getUTCFullYear() &&
        d.getUTCMonth() === now.getUTCMonth() &&
        d.getUTCDate() === now.getUTCDate()
    );
}

function parseRawTime(ev) {
    const raw = ev.raw_value || "";
    if (ev.source_format === "tagheuer") {
        const m = raw.match(/(\d{2}):(\d{2}):(\d{2})\.(\d+)\s+(\d+)\s*$/);
        if (!m) return null;
        const [, hh, mm, ss, frac, days] = m;
        const fracMs = Math.round(Number("0." + frac) * 1000);
        const epoch2000 = Date.UTC(2000, 0, 1);
        const ms =
            epoch2000 +
            Number(days) * 86400000 +
            Number(hh) * 3600000 +
            Number(mm) * 60000 +
            Number(ss) * 1000 +
            fracMs;
        return { ms, kind: "epoch" };
    }
    if (ev.source_format === "microgate") {
        const digits = raw.replace(/[Oo]/g, "0").replace(/[^0-9]/g, "");
        if (digits.length < 44) return null;
        const d = digits.slice(-44);
        const millis = Number(d.slice(28, 36));
        return { ms: millis, kind: "elapsed" };
    }
    return null;
}

function formatDuration(ms) {
    const sign = ms < 0 ? "-" : "";
    ms = Math.abs(ms);
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    const msRest = Math.round(ms % 1000);
    const parts = [];
    if (h > 0) parts.push(String(h).padStart(2, "0"));
    parts.push(String(m).padStart(2, "0"));
    parts.push(String(s).padStart(2, "0"));
    return `${sign}${parts.join(":")}.${String(msRest).padStart(3, "0")}`;
}

export default function Timeserver() {
    const [events, setEvents] = useState([]);
    const [status, setStatus] = useState("connexion en cours…");
    const [statusClass, setStatusClass] = useState("");
    const [sortField, setSortField] = useState("received_at");
    const [sortDir, setSortDir] = useState("desc");
    const [selectedIds, setSelectedIds] = useState([]);
    const [filterChannel, setFilterChannel] = useState("");
    const [filterUser, setFilterUser] = useState("");
    const [wsRef, setWsRef] = useState(null);
    const [seenIds, setSeenIds] = useState(() => new Set());

    async function loadToday() {
        setStatus("chargement…");
        setStatusClass("");
        try {
            const res = await fetch("/api/events/today");
            const data = await res.json();
            setEvents(data);
            setSeenIds(new Set(data.map((e) => e.id).filter(Boolean)));
            setStatus(
                wsRef && wsRef.readyState === WebSocket.OPEN
                    ? "● connecté – mise à jour en direct"
                    : "connexion en cours…"
            );
            setStatusClass(wsRef && wsRef.readyState === WebSocket.OPEN ? "live" : "");
        } catch (e) {
            console.error(e);
            setStatus("erreur de chargement");
            setStatusClass("offline");
        }
    }

    useEffect(() => {
        loadToday();
        const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const ws = new WebSocket(`${wsProtocol}//${window.location.host}`);
        setWsRef(ws);
        ws.onopen = () => {
            setStatus("● connecté – mise à jour en direct");
            setStatusClass("live");
        };
        ws.onclose = () => {
            setStatus("déconnecté – nouvelle tentative dans 3s…");
            setStatusClass("offline");
            setTimeout(() => window.location.reload(), 3000);
        };
        ws.onerror = () => {
            setStatus("erreur de connexion");
            setStatusClass("offline");
        };
        ws.onmessage = (msg) => {
            try {
                const data = JSON.parse(msg.data);
                if (data.type === "new_event") {
                    const ev = data.event;
                    if (!isToday(ev.received_at)) return;
                    setSeenIds((prev) => {
                        const copy = new Set(prev);
                        if (ev.id && copy.has(ev.id)) return copy;
                        if (ev.id) copy.add(ev.id);
                        return copy;
                    });
                    ev._isNew = true;
                    setEvents((prev) => [ev, ...prev]);
                }
            } catch (e) {
                console.error("Erreur parsing WS", e);
            }
        };
        return () => ws.close();
    }, []);

    const channels = useMemo(
        () => [...new Set(events.map((e) => e.channel).filter(Boolean))].sort(),
        [events]
    );
    const users = useMemo(
        () => [...new Set(events.map((e) => e.user).filter(Boolean))].sort(),
        [events]
    );

    const filtered = useMemo(() => {
        let f = events.filter((ev) => {
            if (filterChannel && ev.channel !== filterChannel) return false;
            if (filterUser && ev.user !== filterUser) return false;
            return true;
        });
        f.sort((a, b) => {
            const va = a[sortField];
            const vb = b[sortField];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (sortDir === "asc") return va > vb ? 1 : va < vb ? -1 : 0;
            return va < vb ? 1 : va > vb ? -1 : 0;
        });
        return f;
    }, [events, filterChannel, filterUser, sortField, sortDir]);

    function toggleSelection(id, checked) {
        setSelectedIds((prev) => {
            const copy = [...prev];
            if (checked) {
                if (copy.length >= 2) copy.shift();
                copy.push(id);
            } else {
                const idx = copy.indexOf(id);
                if (idx !== -1) copy.splice(idx, 1);
            }
            return copy;
        });
    }

    function computeDiff() {
        if (selectedIds.length < 2) return null;
        const evA = events.find((e) => e.id === selectedIds[0]);
        const evB = events.find((e) => e.id === selectedIds[1]);
        if (!evA || !evB) return null;
        const rawA = parseRawTime(evA);
        const rawB = parseRawTime(evB);
        let diffMs, precisionNote;
        if (rawA && rawB && rawA.kind === rawB.kind) {
            diffMs = rawB.ms - rawA.ms;
            precisionNote =
                rawA.kind === "epoch"
                    ? "précis, calculé depuis l'horodatage du chronomètre"
                    : "précis, calculé depuis le temps écoulé du chronomètre (Microgate)";
        } else {
            diffMs =
                new Date(evB.received_at).getTime() -
                new Date(evA.received_at).getTime();
            precisionNote =
                "approximatif (heure de réception serveur -- sources non comparables ou non reconnues)";
        }
        return {
            text: formatDuration(diffMs),
            detail: `entre seq ${evA.sequence ?? "?"} [${evA.channel ?? "?"}] et seq ${
                evB.sequence ?? "?"
            } [${evB.channel ?? "?"}] — ${precisionNote}`,
        };
    }

    const diff = computeDiff();

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <ChronoSection>
                <ChronoTitle>Historique du jour – 321 Timing</ChronoTitle>

                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        mb: 2,
                        color:
                            statusClass === "live"
                                ? "success.main"
                                : statusClass === "offline"
                                    ? "error.main"
                                    : "text.secondary",
                    }}
                >
                    {status}
                </Typography>

                {/* PANEL DIFF */}
                {selectedIds.length > 0 && (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: "16px",
                        }}
                    >
                        {diff ? (
                            <>
                                <Typography variant="subtitle2">
                                    Écart entre les deux trames sélectionnées :
                                </Typography>
                                <Typography variant="h5" sx={{ color: "warning.main" }}>
                                    {diff.text}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                                >
                                    {diff.detail}
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="body2">
                                Sélectionnez une 2e trame pour calculer l&apos;écart…
                            </Typography>
                        )}

                        <Box mt={1}>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                onClick={() => setSelectedIds([])}
                            >
                                Effacer la sélection
                            </Button>
                        </Box>
                    </Paper>
                )}

                {/* FILTRES */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        alignItems: "flex-end",
                        mb: 3,
                    }}
                >
                    <FormControl sx={{ minWidth: 160 }}>
                        <InputLabel>Canal</InputLabel>
                        <Select
                            label="Canal"
                            value={filterChannel}
                            onChange={(e) => setFilterChannel(e.target.value)}
                        >
                            <MenuItem value="">Tous</MenuItem>
                            {channels.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 160 }}>
                        <InputLabel>Utilisateur</InputLabel>
                        <Select
                            label="Utilisateur"
                            value={filterUser}
                            onChange={(e) => setFilterUser(e.target.value)}
                        >
                            <MenuItem value="">Tous</MenuItem>
                            {users.map((u) => (
                                <MenuItem key={u} value={u}>
                                    {u}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button variant="contained" onClick={loadToday}>
                        🔄 Recharger tout le jour
                    </Button>
                </Box>

                {/* TABLEAU */}
                {filtered.length === 0 ? (
                    <Alert severity="info">Aucune trame aujourd&apos;hui.</Alert>
                ) : (
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: "16px",
                            overflowX: "auto",
                        }}
                    >
                        <Table
                            size="small"
                            stickyHeader
                            sx={{
                                minWidth: 900,
                                "& th, & td": {
                                    whiteSpace: "nowrap",
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    {[
                                        ["received_at", "Reçu à"],
                                        ["chrono_id", "Chrono"],
                                        ["channel", "Canal"],
                                        ["candidate", "Candidat"],
                                        ["sequence", "Séquence"],
                                        ["source_format", "Source"],
                                        ["user", "Utilisateur"],
                                        ["raw_value", "Brut"],
                                    ].map(([field, label]) => (
                                        <TableCell
                                            key={field}
                                            onClick={() => {
                                                if (sortField === field) {
                                                    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                                                } else {
                                                    setSortField(field);
                                                    setSortDir("desc");
                                                }
                                            }}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            {label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filtered.map((ev) => {
                                    const selected = selectedIds.includes(ev.id);
                                    return (
                                        <TableRow key={ev.id}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selected}
                                                    onChange={(e) =>
                                                        toggleSelection(ev.id, e.target.checked)
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>{ev.received_at ?? ""}</TableCell>
                                            <TableCell>{ev.chrono_id ?? ""}</TableCell>
                                            <TableCell>{ev.channel ?? ""}</TableCell>
                                            <TableCell>{ev.candidate ?? ""}</TableCell>
                                            <TableCell>{ev.sequence ?? ""}</TableCell>
                                            <TableCell>{ev.source_format ?? ""}</TableCell>
                                            <TableCell>{ev.user ?? ""}</TableCell>
                                            <TableCell>{ev.raw_value ?? ""}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </ChronoSection>
        </Container>
    );
}
